/**
 * Display helpers of the work order screens (design S2): durations as the list prints them ("40 min",
 * "5 h", "3 d 4 h"), the due date with its lateness ("10.10 · 5 d late"), initials for the avatars.
 */

import dayjs from 'dayjs';

import type { TFunction } from 'i18next';

/** A duration in minutes, coarse: minutes under an hour, hours under a day, then days and hours. */
export function formatSpan(minutes: number, t: TFunction): string {
  const m = Math.max(0, Math.floor(minutes));
  if (m < 60) return t('workOrders:units.min', { count: m });
  const hours = Math.floor(m / 60);
  if (hours < 24) {
    const rest = m % 60;
    return rest === 0 ? t('workOrders:units.h', { count: hours }) : `${t('workOrders:units.h', { count: hours })} ${t('workOrders:units.min', { count: rest })}`;
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h === 0 ? t('workOrders:units.d', { count: days }) : `${t('workOrders:units.d', { count: days })} ${t('workOrders:units.h', { count: h })}`;
}

/** Minutes since an instant. */
export function minutesSince(iso: string, now = new Date()): number {
  return (now.getTime() - new Date(iso).getTime()) / 60_000;
}

/** "DD.MM", plus the lateness in days when the date has passed. */
export function dueLabel(dueOn: string, today: string, t: TFunction): { text: string; late: boolean } {
  const due = dayjs(dueOn);
  const days = dayjs(today).diff(due, 'day');
  return days > 0
    ? { text: `${due.format('DD.MM')} · ${t('workOrders:list.daysLate', { count: days })}`, late: true }
    : { text: due.format('DD.MM'), late: false };
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')).toUpperCase();
}

/** "M. Ozols" from "Mārtiņš Ozols". */
export function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]?.[0] ?? ''}. ${parts.slice(1).join(' ')}` : name;
}

export function todayRiga(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Riga', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** NET money as the design prints it: "1 384.50". */
export function formatMoney(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  const [int, dec] = amount.toFixed(2).split('.');
  return `${(int ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}.${dec}`;
}
